import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RotateCcw, Save, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { useToast } from '@/lib/hooks/use-toast';
import { adminService } from '../services/admin-service';
import type { PermisosDeEmpresa } from '../types/admin.types';

/**
 * Los permisos por rol de una empresa cliente, desde la plataforma.
 *
 * 🔴 Antes esto solo se podía **impersonando**: entrar como un usuario del
 * cliente, cambiar, y salir. Andaba, pero era un rodeo para algo de soporte
 * diario y dejaba el rastro indirecto — todo lo hecho en esa sesión quedaba a
 * nombre del cliente.
 *
 * **Es la otra capa, y conviene no confundirlas.** Acá se reparte lo que la
 * empresa **tiene**; lo que la empresa **compró** se decide arriba, en el panel
 * de módulos. Un permiso concedido acá sobre un módulo que no está en el techo no
 * sirve de nada: el servidor lo filtra igual. Por eso los módulos fuera del techo
 * se marcan, en vez de dejar que alguien los tilde y crea que hizo algo.
 *
 * **Roles personalizados no se tocan desde acá.** Son del cliente, tienen su
 * propio CRUD con tres estados por módulo, y duplicar esa pantalla sería mantener
 * dos versiones de lo mismo. Para eso sigue estando la impersonación.
 */

interface TenantPermissionsPanelProps {
  tenantId: string;
}

const ETIQUETAS_DE_ROL: Record<string, string> = {
  COMPANY_ADMIN: 'Admin de empresa',
  SALESPERSON: 'Vendedor',
  APPROVER: 'Aprobador',
};

export function TenantPermissionsPanel({ tenantId }: TenantPermissionsPanelProps) {
  const { toast } = useToast();
  const { confirm, ConfirmDialog } = useConfirmDialog();

  const [datos, setDatos] = useState<PermisosDeEmpresa | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState<string | null>(null);
  /*
    El techo de la empresa, para poder avisar qué permisos NO van a tener efecto.

    Se pide acá y no llega por prop: el panel de módulos que está justo arriba lo
    tiene, pero subir ese estado al detalle para bajarlo por dos ramas acopla dos
    componentes que hoy no se conocen. Es una consulta más, y a cambio el panel
    funciona solo donde lo pongan.
  */
  const [techo, setTecho] = useState<string[] | null>(null);
  /*
    Qué pestaña está abierta, controlada y no por omisión.

    🔴 Con `defaultValue` pasaba esto: editabas Vendedor, guardabas, y al
    recargar los datos volvías a Admin de empresa. Uno cree que se equivocó de
    pestaña, o peor, que guardó sobre el rol que no era.
  */
  const [rolAbierto, setRolAbierto] = useState<string>('COMPANY_ADMIN');

  /** Lo marcado ahora, por rol, antes de guardar. */
  const [edicion, setEdicion] = useState<
    Record<string, { permisos: Set<string>; menus: Set<string> }>
  >({});

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [respuesta, modulos] = await Promise.all([
        adminService.getTenantPermissions(tenantId),
        /*
          Si esta falla no se corta nada: sin techo simplemente no se avisa, que es
          el mismo comportamiento que con una empresa sin plan asignado.
        */
        adminService.getTenantModules(tenantId).catch(() => null),
      ]);
      setDatos(respuesta);
      setTecho(modulos?.modules?.length ? modulos.modules : null);
      setEdicion(
        Object.fromEntries(
          respuesta.roles.map((r: PermisosDeEmpresa['roles'][number]) => [
            r.role,
            { permisos: new Set(r.permissions), menus: new Set(r.menuItems) },
          ])
        )
      );
    } catch (err: any) {
      /*
        El error va en la pantalla y no en un toast que se va: si la consulta
        falla, dejar todo desmarcado se lee como "este rol no tiene nada", que es
        exactamente lo contrario de lo que pasó.
      */
      setError(err?.response?.data?.error ?? 'No se pudieron leer los permisos de esta empresa.');
    } finally {
      setCargando(false);
    }
  }, [tenantId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  /** Los módulos que la empresa NO compró: tildarlos acá no hace nada. */
  const fueraDelTecho = useMemo(() => {
    if (!techo || techo.length === 0) return null;
    return new Set(techo);
  }, [techo]);

  function alternar(role: string, grupo: 'permisos' | 'menus', clave: string) {
    setEdicion((prev) => {
      const actual = prev[role];
      if (!actual) return prev;
      const copia = new Set(actual[grupo]);
      if (copia.has(clave)) copia.delete(clave);
      else copia.add(clave);
      return { ...prev, [role]: { ...actual, [grupo]: copia } };
    });
  }

  function hayCambios(role: string): boolean {
    const original = datos?.roles.find((r) => r.role === role);
    const actual = edicion[role];
    if (!original || !actual) return false;
    return (
      original.permissions.length !== actual.permisos.size ||
      original.menuItems.length !== actual.menus.size ||
      original.permissions.some((p) => !actual.permisos.has(p)) ||
      original.menuItems.some((m) => !actual.menus.has(m))
    );
  }

  function guardar(role: string) {
    const actual = edicion[role];
    if (!actual) return;

    /*
      Quitarle el acceso a alguien se confirma. Del otro lado hay personas
      trabajando: si un vendedor pierde Cotizaciones, se entera cuando abre el
      sistema y no puede trabajar.
    */
    const aplicar = async () => {
      setGuardando(role);
      try {
        await adminService.setTenantRolePermissions(
          tenantId,
          role,
          [...actual.permisos],
          [...actual.menus]
        );
        toast({
          title: 'Permisos guardados',
          description: `Rol ${ETIQUETAS_DE_ROL[role] ?? role}.`,
        });
        await cargar();
      } catch (err: any) {
        toast({
          variant: 'destructive',
          title: 'No se pudo guardar',
          description: err?.response?.data?.error ?? 'Ocurrió un error.',
        });
      } finally {
        setGuardando(null);
      }
    };

    const original = datos?.roles.find((r) => r.role === role);
    const quitados = (original?.menuItems ?? []).filter((m) => !actual.menus.has(m)).length;

    // Sumar accesos no se pregunta; quitarlos sí, porque del otro lado hay gente
    // que va a abrir el sistema y no va a poder trabajar.
    if (quitados === 0) {
      aplicar();
      return;
    }

    confirm({
      title: `¿Quitarle ${quitados} ${quitados === 1 ? 'módulo' : 'módulos'} a ${ETIQUETAS_DE_ROL[role] ?? role}?`,
      description: `Las personas con ese rol en ${datos?.tenant.name ?? 'esta empresa'} dejan de verlos, y la API deja de responderles. Queda registrado quién lo hizo.`,
      confirmText: 'Sí, quitar',
      variant: 'destructive',
      onConfirm: aplicar,
    });
  }

  function restablecer(role: string) {
    confirm({
      title: `¿Volver ${ETIQUETAS_DE_ROL[role] ?? role} a los valores por omisión?`,
      description:
        'Se borra lo que esta empresa haya configurado para ese rol y vuelve a lo que trae el sistema. Queda registrado quién lo hizo.',
      confirmText: 'Restablecer',
      onConfirm: async () => {
        setGuardando(role);
        try {
          await adminService.resetTenantRolePermissions(tenantId, role);
          toast({ title: 'Rol restablecido' });
          await cargar();
        } catch (err: any) {
          toast({
            variant: 'destructive',
            title: 'No se pudo restablecer',
            description: err?.response?.data?.error ?? 'Ocurrió un error.',
          });
        } finally {
          setGuardando(null);
        }
      },
    });
  }

  if (cargando) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-destructive">{error}</p>
        <Button variant="outline" size="sm" onClick={cargar}>
          Reintentar
        </Button>
      </div>
    );
  }

  if (!datos) return null;

  return (
    <div className="space-y-4">
      <Tabs value={rolAbierto} onValueChange={setRolAbierto}>
        <TabsList>
          {datos.roles.map((r) => (
            <TabsTrigger key={r.role} value={r.role}>
              {ETIQUETAS_DE_ROL[r.role] ?? r.role}
              {r.isCustomized && (
                <Badge variant="secondary" className="ml-2">
                  editado
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {datos.roles.map((r) => {
          const actual = edicion[r.role];
          if (!actual) return null;

          return (
            <TabsContent key={r.role} value={r.role} className="space-y-6 pt-4">
              {!r.isCustomized && (
                <p className="text-sm text-muted-foreground">
                  Este rol usa los valores por omisión del sistema — esta empresa nunca lo tocó.
                  Guardar acá lo deja fijo y deja de heredar los cambios que se hagan al sistema.
                </p>
              )}

              {/* ── Módulos ── */}
              <div>
                <h4 className="mb-2 text-sm font-medium">Módulos que ve este rol</h4>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {datos.allMenuItems.map((clave) => {
                    const etiqueta = datos.menuItemLabels[clave] ?? clave;
                    const noComprado = fueraDelTecho !== null && !fueraDelTecho.has(clave);
                    return (
                      <label
                        key={clave}
                        className="flex items-start gap-2 text-sm"
                        title={
                          noComprado
                            ? 'La empresa no tiene este módulo contratado: marcarlo acá no tiene efecto.'
                            : undefined
                        }
                      >
                        <Checkbox
                          checked={actual.menus.has(clave)}
                          onCheckedChange={() => alternar(r.role, 'menus', clave)}
                          aria-label={`${etiqueta} para ${ETIQUETAS_DE_ROL[r.role] ?? r.role}`}
                        />
                        <span className={noComprado ? 'text-muted-foreground' : undefined}>
                          {etiqueta}
                          {/*
                            Se avisa en vez de esconderlo: si el módulo desapareciera
                            de la lista, no habría forma de dejar el permiso listo
                            para el día que la empresa sí lo contrate.
                          */}
                          {noComprado && (
                            <span className="block text-xs">no está en el plan de la empresa</span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* ── Acciones ── */}
              <div>
                <h4 className="mb-2 text-sm font-medium">Qué puede hacer</h4>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {datos.allPermissions.map((clave) => {
                    const etiqueta = datos.permissionLabels[clave] ?? clave;
                    return (
                      <label key={clave} className="flex items-start gap-2 text-sm">
                        <Checkbox
                          checked={actual.permisos.has(clave)}
                          onCheckedChange={() => alternar(r.role, 'permisos', clave)}
                          aria-label={`${etiqueta} para ${ETIQUETAS_DE_ROL[r.role] ?? r.role}`}
                        />
                        <span>{etiqueta}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
                <Button
                  onClick={() => guardar(r.role)}
                  disabled={!hayCambios(r.role) || !!guardando}
                >
                  {guardando === r.role ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <Save className="mr-2 h-4 w-4" aria-hidden="true" />
                  )}
                  Guardar {ETIQUETAS_DE_ROL[r.role] ?? r.role}
                </Button>

                {r.isCustomized && (
                  <Button
                    variant="outline"
                    onClick={() => restablecer(r.role)}
                    disabled={!!guardando}
                  >
                    <RotateCcw className="mr-2 h-4 w-4" aria-hidden="true" />
                    Volver a los valores por omisión
                  </Button>
                )}

                {hayCambios(r.role) && (
                  <span className="text-xs text-muted-foreground">Hay cambios sin guardar</span>
                )}
              </div>
            </TabsContent>
          );
        })}
      </Tabs>

      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        Los roles personalizados de esta empresa no se editan desde acá: son suyos y tienen tres
        estados por módulo. Para tocarlos, entrá como uno de sus administradores.
      </p>

      <ConfirmDialog />
    </div>
  );
}

export default TenantPermissionsPanel;
